'use client';

import cx from 'classnames';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../../../../design-system/components/form/button/Button';
import { Agency } from '../../../../../../resources/agency/domain/types/Agency';
import { Destination } from '../../../../../../resources/destination/domain/types/destination';
import { AgencyListSection } from '../../../../../../shared/components/AgencyListSection/AgencyListSection';
import { useUrlGenerator } from '../../../../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../../../../shared/utilities/types/PickDomain';

import style from './Agencies.module.css';

type AgenciesProps = {
    destination: PickDomain<Destination, 'name' | 'slug' | 'preposition' | 'id'>;
    agencies: Array<
        PickDomain<
            Agency,
            | 'id'
            | 'name'
            | 'slug'
            | 'nameTranslationRule'
            | 'tripReviewsAverageRating'
            | 'description'
            | 'picture'
            | 'betterTripIndex'
            | 'isLocalHero',
            {
                destination: PickDomain<Destination, 'slug'>;
            }
        >
    >;
};

export const Agencies = ({ destination, agencies }: AgenciesProps) => {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    const handleClick = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.moreItinerariesClicked({ ...defaultProperties, block: 'no_match' });
        });
    };

    return (
        <div className={style.agenciesBackground}>
            <div className={cx('layout-with-centered-content', style.innerPadding)}>
                <section className="main-container">
                    <AgencyListSection
                        destination={destination}
                        agencies={agencies}
                        showLocalHeroExplanation={false}
                    />
                    <div className={style.text}>
                        {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                        <p className="font-body-bold">
                            {formatMessage({ id: 'page.agency.itineraries-subtitle' })}
                        </p>
                        <Button
                            priority="text"
                            href={generatePath('destination', {
                                destinationSlug: destination.slug,
                            })}
                            onClick={handleClick}
                        >
                            {formatMessage(
                                { id: 'page.agency.itineraries.destination' },
                                { destinationName: destination.name },
                            )}
                        </Button>
                    </div>
                </section>
            </div>
        </div>
    );
};
